import { inject, Injectable } from '@angular/core';

import { createWorkflowNode } from '@blaze-runtime/workflow-domain';
import { type WorkflowNodeType } from '@blaze-shared/api-contracts-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BULK_ACTION_NODE_ID,
  BULK_EXECUTE_NODE_ID,
  type ExecuteWorkflowNode,
  generateId,
  isBulkActionWorkflow,
  isGetNode,
  isServerOnlyWorkflow,
  type WorkflowFormModel,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

/**
 * Creates the node the editor puts on the canvas, working out from the workflow around it what
 * the node should default to.
 */
@Injectable({ providedIn: 'root' })
export class NewWorkflowNodeService {
  private readonly schemaService = inject(SchemaService);

  createNewNode(
    type: WorkflowNodeType,
    previousNodeId: string | undefined,
    wfModel: WorkflowFormModel,
  ): WorkflowNode {
    const nodeIds = wfModel.nodes.ids;
    const isBulkAction = isBulkActionWorkflow(wfModel.workflow);

    // The node that follows a bulk action's execute node *is* its action, and takes a fixed id so
    // that regenerating the action finds it again.
    const isTheBulkActionNode =
      isBulkAction &&
      previousNodeId === BULK_EXECUTE_NODE_ID &&
      !nodeIds.find((id) => id === BULK_ACTION_NODE_ID);

    return createWorkflowNode(type, {
      datasetId: isTheBulkActionNode ? this.bulkActionDatasetId(wfModel) : undefined,
      id: isTheBulkActionNode ? BULK_ACTION_NODE_ID : generateId(nodeIds, type, nodeIds.length),
      isBulkActionNode: isBulkAction,
      isPreviousNodeGetNode:
        isGetNode(wfModel?.workflow?.nodes?.find(({ id }) => id === previousNodeId)) || // saved nodes
        isGetNode(wfModel?.nodes?.entities[previousNodeId || '']), // unsaved nodes
      isServerWorkflow: isServerOnlyWorkflow(wfModel.workflow),
    });
  }

  /** The dataset behind the workflow the bulk action re-runs, which its action node writes to. */
  private bulkActionDatasetId(wfModel: WorkflowFormModel): string | undefined {
    const { workflowId } = wfModel.nodes.entities[BULK_EXECUTE_NODE_ID] as ExecuteWorkflowNode;
    return this.schemaService.getWorkflowSchema(workflowId)?.tableId;
  }
}
